import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi, tmsApi } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { TmsSyncStatus } from '../../types';
import SegmentManager from '../../components/settings/SegmentManager';

export const Settings: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [successMessage, setSuccessMessage] = useState('');
  const [teamsTestResult, setTeamsTestResult] = useState<'success' | 'error' | null>(null);
  const [apiTestResult, setApiTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await settingsApi.get();
      return res.data.data;
    },
  });

  const [formData, setFormData] = useState({
    organizationName: '',
    defaultCurrency: 'DKK',
    timezone: 'Europe/Copenhagen',
    emailNotifications: true,
    taskReminders: true,
    weeklyReport: true,
    // Teams settings
    teamsWebhookUrl: '',
    teamsEnabled: false,
    teamsNotifyNewLead: true,
    teamsNotifyStatusChange: true,
    teamsNotifyTaskAssigned: true,
    teamsNotifyWonLost: true,
    // TMS settings
    tmsApiUrl: '',
    tmsApiKey: '',
    tmsAutoCreateCustomers: true,
    tmsSyncEnabled: false,
    // Customer auto-status
    customerActivityThresholdMonths: 6,
    autoStatusEnabled: true,
    autoStatusSetActive: true,
    autoStatusSetInactive: true,
    autoStatusSetOfferSent: true,
    autoStatusSetContacted: true,
    autoStatusSetLead: true,
    autoStatusProtectLost: true,
    perfActivityTypes: ['CALL', 'EMAIL', 'MEETING', 'NOTE'] as string[],
  });

  const { data: tmsSyncStatus } = useQuery<TmsSyncStatus>({
    queryKey: ['tms-sync-status'],
    queryFn: async () => {
      const res = await tmsApi.getSyncStatus();
      return res.data.data;
    },
    refetchInterval: 30000, // Poll every 30 seconds
  });

  React.useEffect(() => {
    if (settings) {
      setFormData({
        organizationName: settings.organization?.name || '',
        defaultCurrency: settings.defaultCurrency || 'DKK',
        timezone: settings.timezone || 'Europe/Copenhagen',
        emailNotifications: settings.emailNotifications ?? true,
        taskReminders: settings.taskReminders ?? true,
        weeklyReport: settings.weeklyReport ?? true,
        // Teams settings
        teamsWebhookUrl: settings.teamsWebhookUrl || '',
        teamsEnabled: settings.teamsEnabled ?? false,
        teamsNotifyNewLead: settings.teamsNotifyNewLead ?? true,
        teamsNotifyStatusChange: settings.teamsNotifyStatusChange ?? true,
        teamsNotifyTaskAssigned: settings.teamsNotifyTaskAssigned ?? true,
        teamsNotifyWonLost: settings.teamsNotifyWonLost ?? true,
        // TMS settings
        tmsApiUrl: settings.tmsApiUrl || '',
        tmsApiKey: settings.tmsApiKey || '',
        tmsAutoCreateCustomers: settings.tmsAutoCreateCustomers ?? true,
        tmsSyncEnabled: settings.tmsSyncEnabled ?? false,
        // Customer auto-status
        customerActivityThresholdMonths: settings.customerActivityThresholdMonths ?? 6,
        autoStatusEnabled: settings.autoStatusEnabled ?? true,
        autoStatusSetActive: settings.autoStatusSetActive ?? true,
        autoStatusSetInactive: settings.autoStatusSetInactive ?? true,
        autoStatusSetOfferSent: settings.autoStatusSetOfferSent ?? true,
        autoStatusSetContacted: settings.autoStatusSetContacted ?? true,
        autoStatusSetLead: settings.autoStatusSetLead ?? true,
        autoStatusProtectLost: settings.autoStatusProtectLost ?? true,
        perfActivityTypes: settings.perfActivityTypes ?? ['CALL', 'EMAIL', 'MEETING', 'NOTE'],
      });
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => settingsApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['tms-sync-status'] });
      setSuccessMessage('Indstillinger gemt');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  const testTeamsMutation = useMutation({
    mutationFn: (webhookUrl: string) => settingsApi.testTeamsWebhook(webhookUrl),
    onSuccess: () => {
      setTeamsTestResult('success');
      setTimeout(() => setTeamsTestResult(null), 5000);
    },
    onError: () => {
      setTeamsTestResult('error');
      setTimeout(() => setTeamsTestResult(null), 5000);
    },
  });

  const testApiMutation = useMutation({
    mutationFn: () => tmsApi.testConnection(),
    onSuccess: (res) => {
      setApiTestResult(res.data.data);
      setTimeout(() => setApiTestResult(null), 8000);
    },
    onError: () => {
      setApiTestResult({ success: false, message: 'Forbindelsesfejl' });
      setTimeout(() => setApiTestResult(null), 8000);
    },
  });

  const triggerSyncMutation = useMutation({
    mutationFn: () => tmsApi.syncAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tms-sync-status'] });
      setSuccessMessage('Fuld synkronisering gennemført');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
    onError: () => {
      setSuccessMessage('');
    },
  });

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  const handleTestTeams = () => {
    if (formData.teamsWebhookUrl) {
      testTeamsMutation.mutate(formData.teamsWebhookUrl);
    }
  };

  const handleTestApi = () => {
    testApiMutation.mutate();
  };

  const handleTriggerSync = () => {
    triggerSyncMutation.mutate();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={1.5} sx={{ fontSize: '1rem' }}>
        Indstillinger
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 1.5, borderRadius: 1, py: 0.25, fontSize: '0.75rem' }}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        {/* Organization Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: '12px 14px !important' }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5} sx={{ fontSize: '0.8rem' }}>
                Organisation
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="Organisationsnavn"
                value={formData.organizationName}
                onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                sx={{ mb: 1.5 }}
              />

              <Box display="flex" gap={1} mb={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valuta"
                  value={formData.defaultCurrency}
                  onChange={(e) => setFormData({ ...formData, defaultCurrency: e.target.value })}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="Tidszone"
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                />
              </Box>

              <TextField
                fullWidth
                size="small"
                type="number"
                label="Inaktivitetsgrænse (måneder)"
                helperText="Kunder uden aktivitet i denne periode markeres automatisk som inaktive"
                value={formData.customerActivityThresholdMonths}
                onChange={(e) => setFormData({ ...formData, customerActivityThresholdMonths: parseInt(e.target.value) || 6 })}
                slotProps={{ input: { inputProps: { min: 1, max: 24 } } }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Auto-Status Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: '12px 14px !important' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                  Automatisk kundestatus
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={formData.autoStatusEnabled}
                      onChange={(e) => setFormData({ ...formData, autoStatusEnabled: e.target.checked })}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.7rem' }}>Aktiveret</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 1.5 }}>
                Systemet opdaterer automatisk kundestatus ved hver sync baseret på data fra Transpas. Reglerne evalueres i prioriteret rækkefølge — den første regel der matcher, bestemmer kundens status. Du kan slå individuelle regler fra, hvis du vil styre dem manuelt.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1.5, opacity: formData.autoStatusEnabled ? 1 : 0.5 }}>
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem', color: 'text.secondary', mb: 0.25 }}>
                  Statusregler (prioriteret rækkefølge):
                </Typography>

                {[
                  { key: 'autoStatusSetActive' as const, label: 'Aktiv', desc: `Kunden har haft transporter inden for de seneste ${formData.customerActivityThresholdMonths} måneder — dvs. en aktiv samarbejdspartner`, color: '#4caf50' },
                  { key: 'autoStatusSetInactive' as const, label: 'Inaktiv', desc: `Kunden har historiske transporter, men ingen inden for de seneste ${formData.customerActivityThresholdMonths} måneder — et potentielt genaktiveringskontakt`, color: '#9e9e9e' },
                  { key: 'autoStatusSetOfferSent' as const, label: 'Tilbud sendt', desc: 'Kunden har modtaget tilbud fra Kundedatabasen, men har endnu ikke haft transporter — afventer svar', color: '#ff9800' },
                  { key: 'autoStatusSetContacted' as const, label: 'Kontaktet', desc: 'Der er registreret aktiviteter (opkald, emails, møder) på kunden, men ingen tilbud eller transporter endnu', color: '#2196f3' },
                  { key: 'autoStatusSetLead' as const, label: 'Lead', desc: 'Kunden er importeret fra Transpas, men har ingen registrerede aktiviteter, tilbud eller transporter — et ubearbejdet lead', color: '#90caf9' },
                ].map((rule, i) => (
                  <Box key={rule.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, py: 0.25, borderLeft: `3px solid ${rule.color}`, borderRadius: '0 4px 4px 0', bgcolor: formData[rule.key] ? 'transparent' : 'action.disabledBackground' }}>
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', minWidth: 14 }}>{i + 1}.</Typography>
                    <Switch
                      size="small"
                      checked={formData[rule.key]}
                      onChange={(e) => setFormData({ ...formData, [rule.key]: e.target.checked })}
                      disabled={!formData.autoStatusEnabled}
                      sx={{ mr: -0.5 }}
                    />
                    <Box>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>{rule.label}</Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{rule.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch size="small" checked={formData.autoStatusProtectLost}
                      onChange={(e) => setFormData({ ...formData, autoStatusProtectLost: e.target.checked })}
                      disabled={!formData.autoStatusEnabled} />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Beskyt tabte kunder</Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Kunder du manuelt har markeret som "Tabt" forbliver tabte — systemet overrider ikke din vurdering</Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance: Activity Types */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: '12px 14px !important' }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1} sx={{ fontSize: '0.8rem' }}>
                Aktiviteter i medarbejder-performance
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 1.5 }}>
                Vælg hvilke aktivitetstyper der skal tælle med i "Aktiviteter"-kolonnen på Rapporter-siden. Statusændringer og systemhændelser tæller normalt ikke som rigtigt kundearbejde.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {[
                  { key: 'CALL', label: 'Opkald', desc: 'Telefonsamtaler med kunden' },
                  { key: 'EMAIL', label: 'Email', desc: 'Sendte/modtagne emails' },
                  { key: 'MEETING', label: 'Møde', desc: 'Fysiske eller virtuelle møder' },
                  { key: 'NOTE', label: 'Note', desc: 'Manuelle noter på kundekortet' },
                  { key: 'STATUS_CHANGE', label: 'Statusændring', desc: 'Automatisk når kundens status ændres' },
                  { key: 'SYSTEM', label: 'System', desc: 'Automatiske systemhændelser (TMS-import o.l.)' },
                ].map((t) => {
                  const checked = formData.perfActivityTypes.includes(t.key);
                  return (
                    <Box key={t.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                      <Switch
                        size="small"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...formData.perfActivityTypes, t.key]
                            : formData.perfActivityTypes.filter((x: string) => x !== t.key);
                          setFormData({ ...formData, perfActivityTypes: next });
                        }}
                      />
                      <Box>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>{t.label}</Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{t.desc}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: '12px 14px !important' }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1} sx={{ fontSize: '0.8rem' }}>
                Email notifikationer
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.emailNotifications}
                    onChange={(e) => setFormData({ ...formData, emailNotifications: e.target.checked })}
                  />
                }
                label={<Typography sx={{ fontSize: '0.75rem' }}>Email notifikationer</Typography>}
                sx={{ mb: 0.5 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.taskReminders}
                    onChange={(e) => setFormData({ ...formData, taskReminders: e.target.checked })}
                  />
                }
                label={<Typography sx={{ fontSize: '0.75rem' }}>Opgavepåmindelser</Typography>}
                sx={{ mb: 0.5 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.weeklyReport}
                    onChange={(e) => setFormData({ ...formData, weeklyReport: e.target.checked })}
                  />
                }
                label={<Typography sx={{ fontSize: '0.75rem' }}>Ugentlig rapport</Typography>}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Teams Integration */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: '12px 14px !important' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                  Microsoft Teams Integration
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={formData.teamsEnabled}
                      onChange={(e) => setFormData({ ...formData, teamsEnabled: e.target.checked })}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.7rem' }}>Aktiveret</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>

              <Box display="flex" gap={0.5} mb={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Webhook URL"
                  placeholder="https://outlook.office.com/webhook/..."
                  value={formData.teamsWebhookUrl}
                  onChange={(e) => setFormData({ ...formData, teamsWebhookUrl: e.target.value })}
                  disabled={!formData.teamsEnabled}
                />
                <Tooltip title="Test webhook">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleTestTeams}
                      disabled={!formData.teamsWebhookUrl || !formData.teamsEnabled || testTeamsMutation.isPending}
                      sx={{
                        bgcolor: teamsTestResult === 'success' ? 'success.light' : teamsTestResult === 'error' ? 'error.light' : 'action.hover',
                        '&:hover': { bgcolor: 'primary.light' }
                      }}
                    >
                      {testTeamsMutation.isPending ? (
                        <CircularProgress size={16} />
                      ) : teamsTestResult === 'success' ? (
                        <CheckIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                      ) : teamsTestResult === 'error' ? (
                        <ErrorIcon sx={{ fontSize: '1rem', color: 'error.main' }} />
                      ) : (
                        <SendIcon sx={{ fontSize: '1rem' }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mb: 1 }}>
                Send notifikationer til Teams når:
              </Typography>

              <Box display="flex" flexWrap="wrap" gap={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={formData.teamsNotifyNewLead}
                      onChange={(e) => setFormData({ ...formData, teamsNotifyNewLead: e.target.checked })}
                      disabled={!formData.teamsEnabled}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.7rem' }}>Nyt lead</Typography>}
                  sx={{ m: 0, mr: 1 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={formData.teamsNotifyStatusChange}
                      onChange={(e) => setFormData({ ...formData, teamsNotifyStatusChange: e.target.checked })}
                      disabled={!formData.teamsEnabled}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.7rem' }}>Status ændret</Typography>}
                  sx={{ m: 0, mr: 1 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={formData.teamsNotifyTaskAssigned}
                      onChange={(e) => setFormData({ ...formData, teamsNotifyTaskAssigned: e.target.checked })}
                      disabled={!formData.teamsEnabled}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.7rem' }}>Opgave tildelt</Typography>}
                  sx={{ m: 0, mr: 1 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={formData.teamsNotifyWonLost}
                      onChange={(e) => setFormData({ ...formData, teamsNotifyWonLost: e.target.checked })}
                      disabled={!formData.teamsEnabled}
                    />
                  }
                  label={<Typography sx={{ fontSize: '0.7rem' }}>Aktiv/tabt</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* TMS / Flowtale API Integration */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: '12px 14px !important' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                  Flowtale TMS Integration
                </Typography>
                {tmsSyncStatus?.configured ? (
                  <CloudDoneIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
                ) : (
                  <CloudOffIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
                )}
              </Box>

              <TextField
                fullWidth
                size="small"
                label="API URL"
                placeholder="https://prod-e3flowform-api.azurewebsites.net"
                value={formData.tmsApiUrl}
                onChange={(e) => setFormData({ ...formData, tmsApiUrl: e.target.value })}
                sx={{ mb: 1 }}
                slotProps={{ inputLabel: { sx: { fontSize: '0.75rem' } }, input: { sx: { fontSize: '0.75rem' } } }}
              />

              <TextField
                fullWidth
                size="small"
                label="API nøgle"
                type="password"
                value={formData.tmsApiKey}
                onChange={(e) => setFormData({ ...formData, tmsApiKey: e.target.value })}
                sx={{ mb: 1 }}
                slotProps={{ inputLabel: { sx: { fontSize: '0.75rem' } }, input: { sx: { fontSize: '0.75rem' } } }}
              />

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.tmsAutoCreateCustomers}
                    onChange={(e) => setFormData({ ...formData, tmsAutoCreateCustomers: e.target.checked })}
                  />
                }
                label={<Typography sx={{ fontSize: '0.7rem' }}>Auto-opret kunder fra Flowtale</Typography>}
                sx={{ mb: 0.5 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.tmsSyncEnabled}
                    onChange={(e) => setFormData({ ...formData, tmsSyncEnabled: e.target.checked })}
                  />
                }
                label={<Typography sx={{ fontSize: '0.7rem' }}>Automatisk synkronisering (hvert 15 min)</Typography>}
                sx={{ mb: 1 }}
              />

              {tmsSyncStatus?.lastSyncAt && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, fontSize: '0.65rem' }}>
                  Seneste sync: {new Date(tmsSyncStatus.lastSyncAt).toLocaleString('da-DK')}
                </Typography>
              )}

              {(tmsSyncStatus?.unmatchedCustomers || 0) > 0 && (
                <Alert severity="warning" sx={{ mt: 0.5, mb: 1, py: 0, px: 1, fontSize: '0.65rem', borderRadius: 1 }}>
                  {tmsSyncStatus!.unmatchedCustomers} kunder kræver mapping
                </Alert>
              )}

              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleTestApi}
                  disabled={!tmsSyncStatus?.configured || testApiMutation.isPending}
                  startIcon={testApiMutation.isPending ? <CircularProgress size={14} /> : <CloudDoneIcon sx={{ fontSize: '1rem' }} />}
                  sx={{ borderRadius: 1, fontSize: '0.7rem' }}
                >
                  Test forbindelse
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleTriggerSync}
                  disabled={!tmsSyncStatus?.configured || triggerSyncMutation.isPending}
                  startIcon={triggerSyncMutation.isPending ? <CircularProgress size={14} /> : <SyncIcon sx={{ fontSize: '1rem' }} />}
                  sx={{ borderRadius: 1, fontSize: '0.7rem' }}
                >
                  {triggerSyncMutation.isPending ? 'Synkroniserer...' : 'Synkroniser alle data'}
                </Button>
              </Box>

              {apiTestResult && (
                <Alert
                  severity={apiTestResult.success ? 'success' : 'error'}
                  sx={{ mt: 1, py: 0, px: 1, fontSize: '0.65rem', borderRadius: 1 }}
                >
                  {apiTestResult.message}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: '12px 14px !important' }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5} sx={{ fontSize: '0.8rem' }}>
                Din profil
              </Typography>

              <Box display="flex" gap={1} mb={1}>
                <TextField
                  size="small"
                  label="Fornavn"
                  value={user?.firstName || ''}
                  disabled
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Efternavn"
                  value={user?.lastName || ''}
                  disabled
                  fullWidth
                />
              </Box>

              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  value={user?.email || ''}
                  disabled
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Rolle"
                  value={user?.role || ''}
                  disabled
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Password Change */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ borderRadius: 1 }}>
            <CardContent sx={{ p: '12px 14px !important' }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1.5} sx={{ fontSize: '0.8rem' }}>
                Skift adgangskode
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="password"
                label="Nuværende adgangskode"
                sx={{ mb: 1 }}
              />

              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Ny adgangskode"
                />
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Bekræft"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Segments (manual + dynamic geographic) */}
        <Grid size={{ xs: 12 }}>
          <SegmentManager
            canEdit={user?.role === 'ADMIN' || user?.role === 'MANAGER'}
            canDelete={user?.role === 'ADMIN'}
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" mt={1.5}>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={updateMutation.isPending}
          sx={{ borderRadius: 1 }}
        >
          {updateMutation.isPending ? 'Gemmer...' : 'Gem indstillinger'}
        </Button>
      </Box>
    </Box>
  );
};

export default Settings;
